import { useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const LABEL = { accepted: "Handed to WhatsApp", sent: "Sent", delivered: "Delivered", read: "Read", replied: "Replied", failed: "Failed" };
const TONE = { accepted: "text-amber-700", sent: "text-sky-700", delivered: "text-emerald-700", read: "text-emerald-800 font-semibold", replied: "text-emerald-800 font-semibold", failed: "text-rose-600 font-semibold" };

export function WaDeliveryLog({ recent, failedCount, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [retrying, setRetrying] = useState("");
  const retry = async (ids) => {
    setRetrying(ids ? ids[0] : "all");
    try {
      const { data } = await api.post("/whatsapp/retry-failed", { ids });
      const msg = `Resent ${data.sent}, failed again ${data.failed}, skipped ${data.skipped} (old date or already ordered)`;
      data.failed ? toast.warning(msg) : toast.success(msg);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); } finally { setRetrying(""); }
  };
  const check = async () => {
    setBusy(true);
    try { const { data } = await api.post("/whatsapp/check-delivery"); toast.success(data.note || `Checked ${data.checked} number(s), ${data.updated} message(s) updated`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4" data-testid="wa-log-section">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-sm font-semibold">Recent messages and delivery</div>
        <div className="flex flex-wrap gap-2">
          {failedCount > 0 && (
            <Button size="sm" onClick={() => retry(null)} disabled={!!retrying} className="bg-[#D96B43] hover:bg-[#c25a35]" data-testid="wa-retry-all-btn">
              {retrying === "all" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-1 h-4 w-4" />}Resend failed ({failedCount})
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={check} disabled={busy} data-testid="wa-check-delivery-btn">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />}Check delivery now
          </Button>
        </div>
      </div>
      <div className="max-h-72 overflow-y-auto rounded-xl bg-[#FAF4E8] p-3 text-xs space-y-2" data-testid="wa-log">
        {!recent.length && <div className="text-muted-foreground">No messages yet.</div>}
        {recent.map((m) => {
          const s = m.ok === false ? "failed" : m.status || "accepted";
          return (
            <div key={m.id} className="flex flex-wrap items-start justify-between gap-x-3 gap-y-0.5 border-b border-[#E7E2D6] pb-1.5 last:border-0" data-testid={`wa-log-row-${m.id}`}>
              <span className="min-w-0"><span className="font-medium">{m.customer_name}</span> <span className="text-muted-foreground">· {m.kind} · {fmtDateTime(m.created_at)}</span></span>
              <span className={`${TONE[s]} text-right`} data-testid={`wa-log-status-${m.id}`}>{LABEL[s]}{s === "failed" && m.error ? `: ${m.error}` : ""}{m.retry_of ? " (resent)" : ""}
                {s === "failed" && !m.retried && !m.kind.startsWith("test_") && (
                  <button type="button" onClick={() => retry([m.id])} disabled={!!retrying} className="ml-2 font-semibold text-[#D96B43] underline" data-testid={`wa-retry-${m.id}`}>
                    {retrying === m.id ? "Resending..." : "Resend"}
                  </button>
                )}
                {m.retried && <span className="ml-2 text-muted-foreground">Resent</span>}
              </span>
            </div>
          );
        })}
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">"Handed to WhatsApp" means the provider accepted it. Only "Delivered" or "Read" means it reached the phone.</p>
    </div>
  );
}
